import { useShippingStatus } from "features/purchases/useShippingStatus";
import { useSelector } from "store";
import useSWRImmutable from "swr/immutable";
import { z } from "zod/mini";

import { FETCH_STATUS } from "../constants/fetchStatus";
import { INITIAL_LIMIT } from "../constants/initialLimit";
import { convertSettlementToCardProps } from "../functions/convertSettlementToCardProps";
import { fetchSettlements } from "../functions/fetchSettlements";
import { filterSettlementsByPeriod } from "../functions/filterSettlementsByPeriod";
import { getDispatchStatuses } from "../functions/getDispatchStatuses";
import { getShippingLabelStatus } from "../functions/getShippingLabelStatus";

import type { FetchStatus } from "../types/fetchStatus";
import type { ParsedSettlement } from "../types/parsedSettlement";
import type { SettlementHistorySource } from "../types/settlementHistorySource";

const DocumentReferenceLikeSchema = z.object({ id: z.string() });
const DocumentReferenceLikeArraySchema = z.array(DocumentReferenceLikeSchema);

/** 注文の配送参照から配送IDを取り出す。Firestoreの古い注文は配列でないことがあるため、形を検証してから読む */
const extractFulfillmentIds = (settlement: ParsedSettlement): string[] => {
  const parseResult = DocumentReferenceLikeArraySchema.safeParse(
    settlement.fulfillmentRefs
  );
  if (!parseResult.success) return [];
  return parseResult.data.map((reference) => reference.id);
};

type Input = {
  /** falseの間は取得を始めない。取得元の切り替え中に二重取得しないため */
  enabled: boolean;
  isShowingAll: boolean;
  selectedFilter: string;
};

/**
 * Firestoreの注文ドキュメントを直読みする従来の取得元。
 * 全件を取得してからクライアント側で期間の絞り込みと表示件数の制限を行う
 */
export const useSettlementHistoryFromFirestore = (
  input: Input
): SettlementHistorySource => {
  const { enabled, isShowingAll, selectedFilter } = input;
  const user = useSelector((state) => state.auth.user);

  const {
    data: allSettlements,
    error,
    isLoading,
  } = useSWRImmutable<ParsedSettlement[], Error>(
    enabled && user.uid !== "" ? ["settlements", user.uid] : null,
    async () => fetchSettlements(user.uid)
  );

  const fulfillmentFirestoreIds = Array.isArray(allSettlements)
    ? allSettlements.flatMap(extractFulfillmentIds)
    : [];

  const { shippingStatuses } = useShippingStatus(fulfillmentFirestoreIds);

  const fetchStatus: FetchStatus = isLoading
    ? FETCH_STATUS.LOADING
    : error
      ? FETCH_STATUS.ERROR
      : FETCH_STATUS.SUCCESS;

  const settlementYearList = (() => {
    if (!Array.isArray(allSettlements)) return [];

    const countsMap = new Map<number, number>();

    allSettlements.forEach((order) => {
      if (order.createdAt === null || order.createdAt === undefined) return;

      const date = order.createdAt.toDate();
      const year = date.getFullYear();
      countsMap.set(year, (countsMap.get(year) ?? 0) + 1);
    });

    return Array.from(countsMap.entries())
      .map(([year, numOfSettlements]) => ({ year, numOfSettlements }))
      .sort((a, b) => b.year - a.year);
  })();

  const filteredSettlements = Array.isArray(allSettlements)
    ? filterSettlementsByPeriod({
        filter: selectedFilter,
        settlements: allSettlements,
      })
    : [];

  const settlements = isShowingAll
    ? filteredSettlements
    : filteredSettlements.slice(0, INITIAL_LIMIT);

  const allHistoryCards = settlements.map((settlement) => {
    const dispatchStatuses = getDispatchStatuses(
      extractFulfillmentIds(settlement),
      shippingStatuses
    );
    const shippingLabelStatus = getShippingLabelStatus(dispatchStatuses);
    return {
      ...convertSettlementToCardProps(settlement),
      shippingLabelStatus,
    };
  });

  const showLoadMore =
    !isShowingAll && filteredSettlements.length > INITIAL_LIMIT;

  return {
    allHistoryCards,
    fetchStatus,
    settlementYearList,
    showLoadMore,
  };
};
